import React, { useEffect, useState } from 'react'
import {
  Alert,
  Avatar,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf'
import ImageIcon from '@mui/icons-material/Image'
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile'
import moment from 'moment'
import CustomDataGrid from '../../../../../components/DataGrid/CustomDataGrid'
import PDFViewer from '../../../../../components/Modals/PDFViewer'
import { DetailRow } from '../../../../../components/Cards/DetailsBox'
import API from '../../../../../services/API'
import AddNewDocument from './AddNewDocument'

const IMAGE_EXTENSIONS = ['jpg', 'jpeg', 'png', 'gif', 'webp']

const extensionOf = (s3key = '') =>
  s3key.split('?')[0].split('.').pop()?.toLowerCase() ?? ''

const FileTypeIcon = ({ s3key }) => {
  const extension = extensionOf(s3key)
  const sx = { fontSize: 20, flexShrink: 0 }
  if (extension === 'pdf') return <PictureAsPdfIcon color="error" sx={sx} />
  if (IMAGE_EXTENSIONS.includes(extension)) {
    return <ImageIcon color="primary" sx={sx} />
  }
  return <InsertDriveFileIcon color="action" sx={sx} />
}

const uploaderName = (uploadedBy) =>
  `${uploadedBy?.firstName ?? ''} ${uploadedBy?.lastName ?? ''}`.trim() ||
  'Unknown'

// Files kept on the employee's record (contracts, ID, certificates that
// aren't qualifications). The tab header comes from Tabs.jsx.
const DocsTab = ({ employee }) => {
  const [documents, setDocuments] = useState([])
  const [fetchError, setFetchError] = useState(false)
  const [selectedFile, setSelectedFile] = useState(null)
  const [openAddNew, setOpenAddNew] = useState(false)
  const [openConfirmDelete, setOpenConfirmDelete] = useState(false)
  const [deleting, setDeleting] = useState(false)

  const employeeID = employee?.employee?._id

  const loadDocuments = () => {
    if (!employeeID) return
    API.get(`/employee/${employeeID}/documents`)
      .then((res) =>
        res.status === 200 ? setDocuments(res.data) : setFetchError(true),
      )
      .catch(() => setFetchError(true))
  }

  useEffect(() => {
    loadDocuments()
  }, [employeeID])

  const handleDeleteDocument = () => {
    if (!selectedFile) return
    setDeleting(true)
    API.post('/employee/deletedocument', {
      docID: selectedFile._id,
      employeeID,
      s3key: selectedFile.s3key,
    })
      .then((res) => {
        if (res.status === 200) {
          loadDocuments()
          setOpenConfirmDelete(false)
          setSelectedFile(null)
        }
      })
      .finally(() => setDeleting(false))
  }

  const columns = [
    {
      field: 'title',
      headerName: 'Title',
      minWidth: 220,
      flex: 1,
      renderCell: ({ row }) => (
        <Stack direction="row" gap={1} alignItems="center" minWidth={0}>
          <FileTypeIcon s3key={row.s3key} />
          <Typography fontWeight={600} noWrap sx={{ fontSize: '0.9rem' }}>
            {row.title}
          </Typography>
        </Stack>
      ),
    },
    {
      field: 'type',
      headerName: 'Type',
      minWidth: 90,
      valueGetter: (value, row) => extensionOf(row.s3key).toUpperCase(),
      renderCell: ({ value }) =>
        value ? <Chip size="small" variant="outlined" label={value} /> : null,
    },
    {
      field: 'uploadedBy',
      headerName: 'Uploaded by',
      minWidth: 180,
      flex: 0.6,
      valueGetter: (value, row) => uploaderName(row.uploadedBy),
      renderCell: ({ row }) => (
        <Stack gap={1} direction="row" alignItems="center">
          <Avatar
            src={row.uploadedBy?.photograph}
            sx={{ width: 24, height: 24 }}
          />
          <Typography sx={{ fontSize: '0.85rem' }}>
            {uploaderName(row.uploadedBy)}
          </Typography>
        </Stack>
      ),
    },
    {
      field: 'createdAt',
      headerName: 'Date',
      minWidth: 140,
      flex: 0.5,
      valueGetter: (value, row) =>
        row.createdAt ? moment(row.createdAt).format('YYYY-MM-DD HH:mm') : '',
      renderCell: ({ row }) =>
        row.createdAt ? moment(row.createdAt).format('D MMM YYYY') : '—',
    },
  ]

  return (
    <Stack gap={1.5}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'stretch', sm: 'center' }}
        gap={1}
      >
        <Typography variant="body2" color="text.secondary">
          Files kept on {employee?.firstName ?? 'this employee'}'s record. Click
          a document to view or delete it.
        </Typography>
        {!fetchError && (
          <Button
            size="small"
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setOpenAddNew(true)}
            sx={{ whiteSpace: 'nowrap', alignSelf: { sm: 'center' } }}
          >
            Add Documents
          </Button>
        )}
      </Stack>

      {fetchError ? (
        <Alert severity="error">
          Unable to load documents. Please try again later.
        </Alert>
      ) : (
        <CustomDataGrid
          autoHeight
          columns={columns}
          rows={documents}
          getRowId={(row) => row._id ?? row.s3key}
          hideToolbar
          slots={{ footer: () => null }}
          initialState={{
            sorting: { sortModel: [{ field: 'createdAt', sort: 'desc' }] },
          }}
          disableRowSelectionOnClick
          onRowClick={({ row }) => setSelectedFile(row)}
          sx={{ '& .MuiDataGrid-cell': { lineHeight: 1.4 } }}
        />
      )}

      <AddNewDocument
        open={openAddNew}
        onClose={() => setOpenAddNew(false)}
        employeeID={employeeID}
        employeeFirstName={employee?.firstName}
        onUploaded={loadDocuments}
      />

      <PDFViewer
        open={!!selectedFile}
        onClose={() => setSelectedFile(null)}
        title={selectedFile?.title ?? ''}
        s3key={selectedFile?.s3key ?? null}
        filename={`${employee?.lastName ?? ''}${employee?.firstName ?? ''}-${
          selectedFile?.title ?? 'document'
        }.pdf`}
      >
        <Stack
          gap={1}
          direction={{ xs: 'column', lg: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'stretch', lg: 'flex-start' }}
        >
          <Stack>
            <DetailRow
              label="Uploaded"
              value={
                selectedFile?.createdAt
                  ? moment(selectedFile.createdAt).format(
                      'Do MMM YYYY [at] HH:mm',
                    )
                  : '—'
              }
            />
            <DetailRow
              label="Uploaded by"
              value={uploaderName(selectedFile?.uploadedBy)}
            />
          </Stack>
          <Button
            size="small"
            variant="outlined"
            color="error"
            onClick={() => setOpenConfirmDelete(true)}
            sx={{ alignSelf: { xs: 'stretch', lg: 'flex-start' } }}
          >
            Delete document
          </Button>
        </Stack>
      </PDFViewer>

      <Dialog
        open={openConfirmDelete}
        onClose={() => !deleting && setOpenConfirmDelete(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Delete this document?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            "{selectedFile?.title}" will be removed from{' '}
            {employee?.firstName ?? 'this employee'}'s record and the file
            deleted. This cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setOpenConfirmDelete(false)}
            disabled={deleting}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteDocument}
            disabled={deleting}
          >
            {deleting ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  )
}

export default DocsTab
